import Modal from '@advanza/modal'
import style from '@advanza/ui/baseModal.module.css'
import { useModal } from '@ebay/nice-modal-react'
import {
    ComponentPropsWithoutRef,
    createContext,
    CSSProperties,
    PropsWithChildren,
    useContext,
} from 'react'

export enum ScrollArea {
    /**
     *  the whole modal scroll
     */
    BODY = 'body',
    /**
     *  only the part in <BaseModalContent> scrolls
     */
    CONTENT = 'content',
}

interface Props {
    closeOnOutSideClick?: boolean
    modalInsideStyle?: CSSProperties
    mobileFullscreen?: boolean
    /**
     * On mobile (<= 767px) dock the modal to the bottom of the screen as a
     * slide-up bottom sheet (rounded top corners) instead of the default
     * centered dialog. Desktop presentation is unchanged.
     */
    mobileSlideUp?: boolean
    disableCloseButton?: boolean
    hideCloseButton?: boolean
    onClose?: () => void
    onAfterClose?: () => void
    maxWidth?: number | string
    classNames?: string
    /**
     * Extra class applied to the close-button container (the wrapper around
     * the X). Useful for nudging the X to line up with a custom header layout
     * such as a back-button row.
     */
    closeButtonClassName?: string
    /**
     * within the modal, where does the scrollbar appears
     */
    scrollArea?: ScrollArea
    /**
     * Stacking order of the modal overlay. Defaults to the shared modal
     * z-index (101). Raise it when a modal must sit above other modals that
     * may open concurrently (e.g. on-load popups).
     */
    zIndex?: number
}

const BaseModalContext = createContext<{ scrollArea: ScrollArea }>({ scrollArea: ScrollArea.BODY })

const BaseModal = ({
    children,
    classNames,
    closeOnOutSideClick = true,
    modalInsideStyle,
    mobileFullscreen = false,
    mobileSlideUp = false,
    disableCloseButton = false,
    hideCloseButton = false,
    onClose: onCloseProp = undefined,
    onAfterClose,
    scrollArea = ScrollArea.BODY,
    maxWidth,
    closeButtonClassName,
    zIndex,
}: PropsWithChildren<Props>) => {
    const modal = useModal()
    const onClose =
        onCloseProp ||
        (() => {
            modal.resolve()
            modal.hide()
            onAfterClose && onAfterClose()
        })

    return (
        <Modal
            classNames={classNames}
            maxWidth={maxWidth}
            className={[
                mobileFullscreen && style.mobileFullscreen,
                mobileSlideUp && style.mobileSlideUp,
            ]
                .filter(Boolean)
                .join(' ')}
            classObj={{
                modalInside: style.root,
                closeButton: [style.closeButton, closeButtonClassName].filter(Boolean).join(' '),
            }}
            wrapContent={false}
            zIndex={zIndex}
            open={modal.visible}
            close={onClose}
            hideCloseButton={hideCloseButton}
            closeOnOutsideClick={closeOnOutSideClick}
            afterExit={() => modal.remove()}
            modalInsideStyle={{
                // CONTENT supplies a default maxHeight; the caller's style wins so an
                // explicit visual-viewport cap (100dvh) is not overwritten.
                ...(scrollArea === ScrollArea.CONTENT && { maxHeight: '100%' }),
                ...modalInsideStyle,
            }}
            disableCloseButton={disableCloseButton}>
            <BaseModalContext.Provider value={{ scrollArea }}>{children}</BaseModalContext.Provider>
        </Modal>
    )
}

export const BaseModalHeader = ({
    children,
    className,
    ...rest
}: ComponentPropsWithoutRef<'div'>) => (
    <div {...rest} className={[style.header, className].join(' ')}>
        {children}
    </div>
)

export const BaseModalFooter = ({
    children,
    className,
    ...rest
}: ComponentPropsWithoutRef<'div'>) => (
    <div {...rest} className={[style.footer, className].join(' ')}>
        {children}
    </div>
)

export const BaseModalMainContent = ({
    children,
    className,
    style: customStyle,
    ...rest
}: ComponentPropsWithoutRef<'div'>) => {
    const { scrollArea } = useContext(BaseModalContext)
    return (
        <div
            {...rest}
            style={{
                ...(scrollArea === ScrollArea.CONTENT && { overflow: 'auto' }),
                ...customStyle,
            }}
            className={[style.content, className].join(' ')}>
            {children}
        </div>
    )
}

export default BaseModal
